import { Weapon } from '../Weapon.js';
import { WEAPONS } from '../../../../config/constants.js';

/**
 * Glyphpen (Act III, ACT3-BIBLE.md §7) — the first weapon whose output is
 * *positional* rather than directional.
 *
 * Every other weapon in the game answers the question "where is the enemy".
 * The Glyphpen doesn't ask: it writes marks on the floor around the player,
 * each a short-lived pulsing disc, and whatever walks over one takes the hit.
 * It is Quill's starter and reads as her craft in weapon form, but like every
 * starter it is usable by anyone.
 *
 * That makes it structurally unlike the rest of the roster: it is worthless
 * against something standing still at range and excellent in a crowd that has
 * to come to you, which is a trade no other weapon offers. It is also the one
 * weapon that keeps working while the player is kiting backwards through the
 * space they just left.
 *
 * Marks are stationary pooled projectiles with a short lifetime, re-struck on
 * every cast — the same modelling the Umbral Lantern's beam uses, so this needs
 * no new primitive and picks up crits, DoT stacks and every damage multiplier
 * exactly like a normal shot.
 */
export class Glyphpen extends Weapon {
  constructor() {
    super(WEAPONS.glyphpen);
  }

  /** Marks per cast: 2 at level 1, one more at each of levels 4 and 7. */
  get markCount() {
    return this.config.marks + (this.level >= 4 ? 1 : 0) + (this.level >= 7 ? 1 : 0);
  }

  _markDamage() {
    return this.config.damage + this.level * 2.2;
  }

  fire(origin, target, pool) {
    // Marks are laid between the player and whatever they're aimed at, so the
    // field builds along the line of engagement rather than uselessly behind.
    const angle = target
      ? Math.atan2(target.y - origin.y, target.x - origin.x)
      : Math.atan2(origin.facing?.y ?? -1, origin.facing?.x ?? 0);
    for (let i = 0; i < this.markCount; i++) {
      // Fanned out around that line, at staggered distances, so a cast covers
      // an area rather than stacking every mark on one point.
      const spread = ((i / Math.max(1, this.markCount - 1)) - 0.5) * 1.1;
      const dist = this.config.range * (0.45 + 0.55 * ((i % 2) === 0 ? 1 : 0.6));
      pool.fire({
        x: origin.x + Math.cos(angle + spread) * dist,
        y: origin.y + Math.sin(angle + spread) * dist,
        vx: 0,
        vy: 0,
        radius: this.config.projectileRadius,
        damage: this._markDamage(),
        hits: 1,
        lifetime: this.config.lifetime,
        color: this.config.color,
      });
    }
    // Lets JuiceDirector scale its reaction to the written area rather than
    // giving a floor-wide cast the same feedback as a single pellet.
    this.lastBurstRadius = this.config.range;
  }
}
